'use client';

import { styled } from '@linaria/react';
import { fillZero } from '@fe-common-utils/libs/time';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { PopTopicListData, GTM_EVENT_POP_TOPIC_CAROUSEL } from '@fe-news/constants/pop-topic/type';
import Link from 'next/link';
import { useImperativeHandle, useRef, forwardRef } from 'react';
import { CustomEventName, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';

const Headline = styled.div<{ isMonthCard: boolean; isYearChange: boolean }>`
  display: flex;
  color: #54b5c6;
  align-items: center;
  margin: ${({ isYearChange }) => (isYearChange ? '35px 0px' : '0px')};
  min-height: ${({ isMonthCard }) => (isMonthCard ? '36px' : '46px')};

  .time {
    position: ${({ isYearChange }) => (isYearChange ? 'absolute' : 'relative')};
    padding: ${({ isYearChange }) => (isYearChange ? '24px 0px' : '0px')};
    background-color: ${({ isYearChange }) => (isYearChange ? '#fff' : '')};
    font-size: ${({ isYearChange, isMonthCard }) => (isYearChange ? '20px' : isMonthCard ? '18px' : '16px')};
    margin-left: ${({ isMonthCard }) => (isMonthCard ? '24px' : '8px')};
    letter-spacing: 0.5px;
    font-weight: ${({ isMonthCard, isYearChange }) => (isYearChange ? 600 : isMonthCard ? '900' : 'bold')};
    z-index: 1;
    left: ${({ isYearChange }) => (isYearChange ? '-29px' : '0px')};
    line-height: ${({ isYearChange }) => (isYearChange ? '1.2' : '1')};
    color: ${({ isYearChange }) => (isYearChange ? '#515c69' : '#54b5c6')};
  }
`;

const Title = styled.div<{ isSameDayBefore: boolean; isPin: boolean }>`
  display: flex;
  padding: 13px 20px 13px 8px;
  color: #383838;
  font-size: 20px;
  font-weight: 600;
  margin-left: ${({ isSameDayBefore }) => (isSameDayBefore ? '78px' : '12px')};
  border-radius: 6px;
  background-color: ${({ isPin }) => (isPin ? '#ddbe431f' : '#f6f8fc')};
  line-height: 1;
  align-items: center;

  span {
    margin-right: 5px;
  }
`;

const Circle = styled.div`
  min-width: 12px;
  height: 12px;
  background-color: #54b5c6;
  border-radius: 50%;
`;

const HollowCircle = styled.div`
  width: 20px;
  height: 20px;
  background-color: #fff;
  border: solid 6px #007d93;
  border-radius: 50%;

  position: absolute;
  z-index: 1;
  left: -5px;
`;

const TitleLink = styled(Link)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;

  &:hover {
    color: #000;
    text-decoration: underline;
    cursor: pointer;
    font-size: 20px;
    font-weight: 600;
  }

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    -webkit-line-clamp: 2;
    line-height: 1.5;
  }
`;

type TimelineProps = {
  data: PopTopicListData;
  ref?: React.RefObject<HTMLElement>;
};

// eslint-disable-next-line react/display-name
const Timeline = forwardRef<HTMLDivElement, TimelineProps>(
  (
    { data },
    // eslint-disable-next-line unicorn/prevent-abbreviations
    ref
  ) => {
    const { title, isYearChange = false, isSameDayBefore = false, id, isPin, eventAt } = data;

    const localReference = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line unicorn/prevent-abbreviations
    useImperativeHandle(ref, () => localReference.current as HTMLDivElement);

    const isMonthCard = title === 'isDefault';

    const date = isYearChange
      ? `${new Date(eventAt).getFullYear()}年`
      : isMonthCard
        ? `${new Date(eventAt).getMonth() + 1}月`
        : `${fillZero(new Date(eventAt).getMonth() + 1)}/${fillZero(new Date(eventAt).getDate())}`;

    const onClickTitle = () => {
      sendGTM({
        ...GTM_EVENT_POP_TOPIC_CAROUSEL,
        [GTMEventKey.CLICK_ITEM]: `時間軸_${title}`,
        [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_POP
      });
    };

    return (
      <Headline isYearChange={isYearChange} isMonthCard={isMonthCard} ref={localReference}>
        {isYearChange || isSameDayBefore ? null : isMonthCard ? <HollowCircle /> : <Circle />}
        {!isSameDayBefore && <div className='time'>{date}</div>}
        {!isMonthCard && (
          <Title isSameDayBefore={isSameDayBefore} isPin={Boolean(isPin)}>
            <span role='img' aria-label='fire-emoji'>
              {isPin ? '🏅' : '🔥'}
            </span>
            <TitleLink href={`poptopic/${id}`} onClick={onClickTitle} prefetch={false}>
              {title}
            </TitleLink>
          </Title>
        )}
      </Headline>
    );
  }
);

export default Timeline;
